<template>
    <div ref="box" class="hw-box">
        <div class="title-bar">
            <led-timer v-if="timerRequired" type="countdown" :time-in-seconds="timeInSeconds" />
            <div class="title">{{ title }}</div>
            <div class="stat-info">
                <span class="label">已提交</span>
                <span class="data">{{ results.length }}</span>
            </div>
        </div>

        <div v-if="results.length > 0 && currentLayer === 'result-list'" class="content-box">
            <div class="op-bar">
                <div class="filter-by">{{ filterLabel }} ({{ shownResults.length }})</div>
                <div class="sort-by asc">{{ sortByLabel }}</div>
                <div class="sort-by-indicator" :class="{'desc': sortType === 'DESC'}" />
            </div>
            <div class="results-container">
                <div v-for="r in shownResults" :key="r.id" class="result-box" :class="{'team': teamType !== 'NO'}">
                    <div v-if="teamType === 'NO'" class="user-row">
                        <img :src="r.user.avatarUrl" alt="" class="avatar">
                        <div class="name-sno">{{ r.user.fullName }}（{{ r.user.studentNo }}）</div>
                    </div>

                    <div v-if="teamType !== 'NO'" class="user-row">
                        <div class="name-sno">{{ r.teamTitle }}（{{ r.memberCount }} 人）</div>
                    </div>

                    <div v-if="r.content != null && r.content.length > 0" class="content" v-html="r.expanded ? r.content : r.truncatedContent"></div>

                    <div v-if="r.attachments && r.attachments.length > 0" class="atta-box" :class="{'empty-content': r.content == null || r.content.length === 0}">
                        <div v-for="a in r.attachments" :key="a.relationId" class="atta" :class="getAttachmentClass(a)">
                            <img v-if="a.thumbnailUrl" :src="a.thumbnailUrl" alt="a.name">
                        </div>
                    </div>

                    <div class="info">
                        <div class="left-col">
                            <!-- 正常提交 -->
                            <span v-if="r.allowReSubmit === 'N' && r.resubmitFlag === 'N'">{{ simplifyTime(r.lastSubmitTime) }} 提交</span>
                            <span v-if="deadlineTime != null && r.allowReSubmit === 'N' && r.resubmitFlag === 'N'">，</span>
                            
                            <!-- 重新提交 -->
                            <span v-if="r.allowReSubmit === 'N' && r.resubmitFlag === 'Y'" class="warning">{{ simplifyTime(r.lastSubmitTime) }} 重交</span>
                            <span v-if="deadlineTime != null && r.allowReSubmit === 'N' && r.resubmitFlag === 'Y'" class="warning">，</span>
                            
                            <span v-if="deadlineTime != null && r.allowReSubmit == 'N'" :class="{'warning': r.lastSubmitTime > deadlineTime}">
                                {{ (r.lastSubmitTime <= deadlineTime) ? '提前' : '超时' }}
                                {{ formatTimeDiff(r.lastSubmitTime, deadlineTime) }}
                            </span>
                            
                            <span v-if="r.allowReSubmit === 'Y' && r.resubmitFlag === 'N'" class="warning">作业处于修改状态</span>
                            <span>&nbsp;&nbsp;&nbsp;&nbsp;评论（{{ r.commentCount }}）</span>
                        </div>
                        <div class="right-col">
                            <!-- 教师评分，这个最简单 -->
                            <span v-if="appraiseType === 'TEACHER'">
                                <span>老师评分</span>
                                <span :class="{'score': r.appraiseScore >= 0}">
                                    {{ r.appraiseScore >= 0 ? r.appraiseScore : '--' }}
                                </span>
                                <span>分</span>
                            </span>
                            
                            <!-- 指定人评分，这个如果是有老师评分，老师的分数作为最终得分 -->
                            <span v-if="appraiseType === 'APPRAISER'">
                                <span>助教/学生评分</span>
                                <span :class="{'score': r.appraiseScore >= 0}">
                                    {{ r.appraiseScore >= 0 ? Math.round(r.appraiseScore) : '--' }}
                                </span>
                                <span>分&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                <span>最终得分</span>
                                <span :class="{'score': r.appraiseScore >= 0 || r.teacherAppraiseScore >= 0}">
                                    {{ r.teacherAppraiseScore >= 0 ? r.teacherAppraiseScore : (r.appraiseScore >= 0 ? Math.round(r. appraiseScore) :'--') }}
                                </span>
                                <span>分</span>
                            </span>

                            <!-- 互评状态下，仅在评价中以及结束之后才有分数 -->
                            <span v-if="appraiseType === 'EACH_OTHER' && (status === 'APPRAISEING' || status === 'END')">
                                <span>{{ anonymousFlag === 'Y' ? '匿名' : '学生' }}互评</span>
                                <span :class="{'score': r.appraiseScore >= 0}">
                                    {{ r.appraiseScore >= 0 ? Math.round(r.appraiseScore) : '--' }}
                                </span>
                                <span>分&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                <span>最终得分</span>
                                <span :class="{'score': r.appraiseScore >= 0 || r.teacherAppraiseScore >= 0}">
                                    {{ r.teacherAppraiseScore >= 0 ? r.teacherAppraiseScore : (r.appraiseScore >= 0 ? Math.round(r. appraiseScore) :'--') }}
                                </span>
                                <span>分</span>
                            </span>
                            
                            <!-- 混合评价，仅在评价中以及结束之后才有分数 -->
                            <span v-if="appraiseType === 'MIX' && (status === 'APPRAISEING' || status === 'END')">
                                <span v-if="isTeacherInMix">
                                    <span>老师评分</span>
                                    <span :class="{'score': r.mixTeacherAppraiseScore >= 0}">
                                        {{ r.mixTeacherAppraiseScore >= 0 ? r.mixTeacherAppraiseScore : '--' }}
                                    </span>
                                    <span>分</span>
                                </span>

                                <span v-if="isTeamOuterInMix">
                                    <span v-if="isTeacherInMix">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                    <span>组间互评</span>
                                    <span :class="{'score': r.mixTeamOuterAppraiseScore >= 0}">
                                        {{ r.mixTeamOuterAppraiseScore >= 0 ? Math.round(r.mixTeamOuterAppraiseScore) : '--' }}
                                    </span>
                                    <span>分</span>
                                </span>

                                <span v-if="isTeamInterInMix">
                                    <span v-if="isTeacherInMix || isTeamOuterInMix">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                    <span v-if="teamType === 'NO'">
                                        <span>组内互评</span>
                                        <span :class="{'score': r.mixTeamInterAppraiseScore >= 0}">
                                            {{ r.mixTeamInterAppraiseScore >= 0 ? Math.round(r.mixTeamInterAppraiseScore) : '--' }}
                                        </span>
                                        <span>分</span>
                                    </span>

                                    <span v-if="teamType !== 'NO'">
                                        <span>组内互评</span>
                                        <span :class="{'score': r.mixTeamInterAppraiseCount > 0}">
                                            {{ r.mixTeamInterAppraiseCount > 0 ? r.mixTeamInterAppraiseCount : '--' }}
                                        </span>
                                        <span>人参与</span>
                                    </span>
                                </span>

                                <span v-if="teamType === 'NO' && status === 'END'">
                                    <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                    <span>最终得分</span>
                                    <span :class="{'score': calcFinalScore(r) >= 0}">
                                        {{ calcFinalScore(r) >= 0 ? Math.round(calcFinalScore(r)) : '--' }}
                                    </span>
                                    <span>分</span>
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="results.length === 0" class="hint-box">
            <div class="hint-content">{{ hintContent }}</div>
        </div>

        <div v-if="currentLayer === 'result-detail'" class="result-detail-layer">
            <div class="result-detail-box" :class="{'team': teamType !== 'NO'}">
                <div class="detail-info">
                    <img v-if="teamType === 'NO'" :src="currentResult.user.avatarUrl" alt="" class="avatar">
                    <div class="detail-name-sno-box">
                        <div>{{ teamType === 'NO' ? currentResult.user.fullName : currentResult.teamTitle }}</div>
                        <div>{{ teamType === 'NO' ? currentResult.user.studentNo : currentResult.teamMembers.length + ' 人' }}</div>
                    </div>
                </div>
                <div class="content" v-html="currentResult.content"></div>
            </div>
        </div>

        <div v-if="hasFiles" :class="{'user-info-opacity': laters}" class="user-info">
            <div v-if="hwFilesData.team.memberCount !== 1 ? false : true">
                <img :src="hwFilesData.team.avatarUrl !== null ? hwFilesData.team.avatarUrl : defaultAvatar" class="user-info-img">
            </div>
            <!-- (hwFilesData.team.avatarUrl === null ? hwFilesData.team.avatarUrl : defaultAvatar) -->
            <p class="user-info-name" > {{hwFilesData.team.title}} </p>
            <p class="user-info-num" > ({{hwFilesData.team.memberCount === 1 ? hwFilesData.team.studentNo : hwFilesData.team.memberCount}}) </p>
        </div>

        <resourceImage id="hw-img" v-if="filesType === 'image'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
        <resourceAudio id="hw-audio" v-if="filesType === 'audio'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
        <resourceVideo id="hw-1" v-if="filesType === 'vidio'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
        <resourceText id="hw-text" v-if="filesType === 'text'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
        <resourcePDF id="hw-pdf" v-if="filesType === 'pdf'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
        <nonsupport id="hw-nonsupport" v-if="filesType === 'nonsupport'" :current-show="currentShow" :hwFilesData="hwFilesData" :isLock="isLock"/>
    </div>
</template>

<script type="text/javascript">
import Vue from 'vue';

import ledTimer from './ledTimer.vue';
import tool from '../assets/scripts/tool.js';
import moment from 'moment';

import defaultAvatar from '../assets/image/default-avatar.png';

import resourceImage from '@/components/resourceImage.vue';
import resourceAudio from '@/components/resourceAudio.vue';
import resourceVideo from '@/components/resourceVideo.vue';
import nonsupport from '@/components/nonsupport.vue';
import resourcePDF from '@/components/resourcePDF.vue';
import resourceText from '@/components/resourceText.vue';


export default {
    components: {
        ledTimer,
        resourceImage,
        resourceAudio,
        resourceVideo,
        resourceText,
        resourcePDF,
        nonsupport
    },

    props: {
        isLock: {
            type: Boolean,
            default: false
        },
        currentShow: {
            type: String,
            default: ''
        }
    },
    
    data: function() {
        return {
            // biz data
            id: null,
            clazzCourseId: null,
            createrId: null,
            componentId: null,
            title: 'This is a test homework',
            content: 'this is the content of the homework',
            createTime: null,
            startTime: null,
            endTime: null,
            status: null,
            autoEnd: null,
            autoEndTime: null,
            deadlineTime: null,
            fullScore: 0,
            appraiseType: null,
            teamType: 'NO',
            teamDivideOption: null,
            mixAppraiseItems: [],
            mixPlan: {
                TEACHER: 0,
                TEAM_OUTER: 0,
                TEAM_INTER: 0
            }, // generated from mix appraise items array to object, the key is the type, the value is the weight
            results: [],
            shownResults: [],

            // ui-related data
            defaultAvatar: defaultAvatar,
            timeInSeconds: 0,
            timerRequired: false,
            timerId: 0,
            serverTimestamp: 0,
            filterType: 'ALL',
            sortBy: 'SUBMIT_TIME',
            sortType: 'ASC',
            currentLayer: 'result-list',
            currentResult: null,
            supportEventNames: [
                'hw.showResults',
                'hw.filterResults',
                'hw.sortResults',
                'hw.rejectResult',
                'hw.expandResultText',
                'hw.collapseResultText',
                'hw.viewResultText',
                'hw.hideResultText',
                'hw.resultScoreChanged',
                'hw.rejectResult',
                'hw.newResult',
                'hw.commentCountChanged',
                'common.moveJoystick',
                'files.zoom',
                'hw.files.show',
                'hw.files.hide'
            ],
            laters: false,
            hasFiles: false,
            filesType: '',
            hwFilesData: {}
        };
    },

    computed: {
        hintContent: function() {
            return this.status === 'END' ? '很遗憾，没有人提交作业~' : '快来提交作业吧～';
        },

        hintBoxStyleString: function() {
            return 'height:' + (window.innerHeight - 80) + 'px;';
        },

        filterLabel: function() {
            const map = {
                'ALL': '已提交',
                'APPRAISED': this.appraiseType === 'MIX' ? '已完全评分' : '已评分',
                'NOT_APPRAISE': this.appraiseType === 'MIX' ? '未完全评分' : '未评分'
            };
            return (this.filterType in map) ? map[this.filterType] : 'UNKNOWN';
        },

        sortByLabel: function() {
            if (this.sortBy === 'STUDENT_NO') return this.teamType === 'NO' ? '按学号排序' : '按组名排序';
            if (this.sortBy === 'SUBMIT_TIME') return '按提交时间排序';
            if (this.sortBy === 'SCORE') return '按得分排序';
            return 'ERROR~~~~~';
        },

        isTeacherInMix: function() {
            return this.mixPlan.TEACHER > 0.0;
        },

        isTeamOuterInMix: function() {
            return this.mixPlan.TEAM_OUTER > 0.0;
        },

        isTeamInterInMix: function() {
            return this.mixPlan.TEAM_INTER > 0.0;  
        }
    },

    watch: {
        currentShow: function(newVal, oldVal) {
            if (newVal != this.componentId && oldVal === this.componentId) {
                if (this.timerId > 0) clearTimeout(this.timerId);
                this.id = null;
                this.title = null;
                this.filterType = 'ALL';
                this.sortBy = 'SUBMIT_TIME';
                this.sortType = 'ASC';
                this.$refs.box.scrollTop = 0;
                this.timeInSeconds = 0;
                this.timerRequired = false;
                this.filesType = '';
            }
        }
    },

    created: function() {
        window.addEventListener('message', this.windowMessageHandler);
    },

    mounted: function() {
        this.componentId = this.$attrs.id;
    },

    destroyed: function() {
        window.removeEventListener('message', this.windowMessageHandler);
    },

    methods: {
        windowMessageHandler: function(e) {
            const msg = e.data;
            if ( ! msg) return;
            const eventName = msg.eventName;
            if ( ! eventName) return;
            const msgData = msg.data;
            // console.log(eventName, msgData);
            switch (eventName) {
                case 'hw.showResults':
                    if (! this.isLock) {
                        this.showResults(msgData);
                    }
                    break;
                case 'hw.filterResults':
                    if (!this.isLock && msgData.id === this.id) {
                        this.filterResults(msgData);
                    }
                    break;
                case 'hw.sortResults':
                    if (!this.isLock && msgData.id === this.id) {
                        this.sortResults(msgData);
                    }
                    break;
                case 'hw.expandResultText':
                    if (!this.isLock && msgData.id === this.id) {
                        this.expandResultText(msgData);
                    }
                    break;
                case 'hw.collapseResultText':
                    if (!this.isLock && msgData.id === this.id) {
                        this.collapseResultText(msgData);
                    }
                    break;
                case 'hw.viewResultText':
                    if (!this.isLock && msgData.id === this.id) {
                        this.viewResultText(msgData);
                    }
                    break;
                case 'hw.hideResultText':
                    if (!this.isLock && msgData.id === this.id) {
                        this.hideResultText();
                    }
                    return;
                case 'hw.resultScoreChanged':
                    if (msgData.homeworkId === this.id) {
                        this.updateScore(msgData);
                    }
                    break;
                case 'hw.rejectResult':
                    if (msgData.id === this.id) {
                        this.rejectResult(msgData);
                    }
                    break;
                case 'hw.newResult':
                    if (msgData.homeworkId === this.id) {
                        this.newResult(msgData);
                    }
                    break;
                case 'common.moveJoystick':
                    this.moveJoystick(msgData);
                    break;
                // case 'files.zoom':
                //     this.zoomTo(msgData);
                //     break;
                case 'hw.commentCountChanged':
                    if (msgData.id === this.id) {
                        this.commentCountChanged(msgData);
                    }
                    break;
                case 'hw.files.show':
                    // if (msgData.entityId !== this.id) return;
                    if (this.isLock) return;
                    this.handleHwFilesShow(msgData);
                    break;
                case 'hw.files.hide':
                    // if (msgData.id !== this.)
                    if (this.isLock) return;
                    this.handleHwFilesHide(msgData);
                default:
                    // statements_def
                    break;
            }
        },  

        /**
         * Simplify the time to MM DD HH:mm format
         * @param  {String} d ISO date time string
         * @return {string}   simplified version
         */
        simplifyTime: function(d) {
            if ( ! d) return;
            const m = moment(d);
            return m.format('M 月 D 日 HH:mm');
        },

        /**
         * Format time diff to N 天 M 小时 P 分钟 format
         * @param  {String} d1 time1 ISO date time string
         * @param  {String} d2 time2 ISO date time string
         * @return {String}    N 天 M 小时 P 分钟 format
         */
        formatTimeDiff: function(dtstring1, dtstring2) {
            if (! dtstring1 || ! dtstring2) return '';
            const d1 = new Date(dtstring1);
            const d2 = new Date(dtstring2);
            let diff = Math.round(Math.abs(d1.getTime() - d2.getTime()) / 1000);

            let d = 0, h = 0, m = 0;

            if (diff >= 86400) {
                d = Math.floor(diff / 86400);
                diff -= d * 86400;
            }
            if (diff >= 3600) {
                h = Math.floor(diff / 3600);
                diff -= h * 3600;
            }
            
            m = diff <= 60 ? 1 : Math.round(diff / 60);

            let s = '';
            if (d > 0) s += (' ' + d + ' 天');
            if (h > 0) s += (' ' + h + ' 小时');
            if (m > 0) s += (' ' + m + ' 分钟');
            return s;
        },

        /**
         * Get an attachment's icon css class
         * @param  {Object} atta the attament information
         * @return {String}      the css class. if can not find the css class, return empty string
         */
        getAttachmentClass: function(atta) {
            if (! atta) return '';
            let mimeType = atta.mimeType;
            let extName = atta.extName;
            if (mimeType) {
                if (mimeType.startsWith('image/')) return '';
                if (mimeType.startsWith('video/')) return 'video';
                if (mimeType.startsWith('audio/')) return 'audio';
                if (mimeType.startsWith('text/')) return 'text';
            }

            const map = {
                'word': ['.doc', '.docx', '.txt', '.wps', '.wpt', '.dot', '.rtf', '.dotx', '.docm', '.dotm'],
                'excel': ['.xls', '.xlt', '.xlsx', '.xml', '.xltm', '.xlsm', '.xlsb', '.xlam', '.xla', '.xlw', '.et'],
                'ppt': ['.ppt', '.pptx', '.pps', '.dps', '.dpt', '.pot', '.potx'],
                'pdf': ['.pdf'],
                'zip': ['.zip', '.rar', '.tar', '.cab', '.uue', '.jar', '.iso', '.z', '.7-zip', '.ace', '.lzh', '.arj', '.gzip', '.bz2']
            };

            if (extName) {
                extName = extName.toLowerCase();
                for (let p in map) {
                    if (map[p].indexOf(extName) != -1) return p;
                }
            }

            return 'other';
        },

        /**
         * 评论数量改变
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-18}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        commentCountChanged: function(msgData) {
            if (! msgData || ! msgData.teamId) return;
            const teamId = msgData.teamId;
            const r = this.results.find(r => r.teamId == teamId);
            if (r) {
                r.commentCount = msgData.commentCount;
            }
        },

        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-9}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        newResult: function(msgData) {
            if ( ! msgData || ! msgData.result) return;
            const result = JSON.parse(JSON.stringify(msgData.result));
            this.preprocessResult(result);
            const index = this.results.findIndex(r => r.teamId == result.teamId);
            if (index >= 0) {
                this.results.splice(index, 1);
            }
            this.results.push(result);
            this.filterResults({
                filterType: this.filterType
            });
        },

        /**
         * Preprocess the result, set some additional properties
         * Only showResults message and newResult message should use this method
         * to initialize some properties on the result for further use
         * @param  {Object} r a result
         * @return {void}   the result will be modified in place.
         */
        preprocessResult: function(r) {
            if (! r) return;

            if (this.teamType === 'NO') {
                if (r.teamMembers.length > 0) {
                    r.user = JSON.parse(JSON.stringify(r.teamMembers[0]));
                    if (! r.user.avatarUrl) r.user.avatarUrl = this.defaultAvatar;
                } else {
                    r.user = {
                        id: '-1',
                        fullName: 'MUST BE WRONG',
                        studentNo: '000000',
                        avatarUrl: this.defaultAvatar
                    };
                }    
            }

            // force reset the expand status
            r.expanded = false;
            if (r.content && r.content.length > 200) {
                r.truncatedContent = tool.htmlspecialchars(r.content.substring(0, 200) + '...');
                r.content = tool.htmlspecialchars(r.content);
            } else {
                r.truncatedContent = tool.htmlspecialchars(r.content);
                r.content = r.truncatedContent;
            }
        },

        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-4}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        rejectResult: function(msgData) {
            if (! msgData) return;
            const teamId = msgData.teamId;
            if (! teamId) return;

            const r = this.results.find(r => r.teamId == teamId);
            if (! r) {
                console.log('hw.rejectResult message recevied, but can not find team id', teamId, 'in current loaded results');
                return;
            }

            r.allowReSubmit = 'Y';
            r.resubmitFlag = 'N';
        },

        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-7}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        viewResultText: function(msgData) {
            if (! msgData) return;

            const teamId = msgData.teamId;
            if (! teamId) return;
            const r = this.shownResults.find(r => r.teamId == teamId);
            if (! r) {
                console.log('hw.viewResultText, can not find result in shown results, teamId:', teamId);
                return;
            }

            this.currentResult = r;
            this.currentLayer = 'result-detail';

            Vue.nextTick(() => {
                this.$refs.box.scrollTop = 0;
            });
        },

        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-8}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        hideResultText: function() {
            this.currentResult = null;
            this.currentLayer = 'result-list';
        },

        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-10}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        updateScore: function(msgData) {
            if (! msgData) return;
            const results = msgData.results;
            const properties = [
                'id',
                'appraiseScore',
                'teacherAppraiseScore',
                'mixTeacherAppraiseScore',
                'mixTeamInterAppraiseScore',
                'mixTeamInterAppraiseCount',
                'mixTeamOuterAppraiseScore'
            ];
            results.forEach(r => {
                const oldResult = this.results.find(oldr => oldr.teamId == r.teamId);
                if (! oldResult) {
                    console.log('score changed message recevied, but can not find team id', r.teamId, 'in current results');
                    return;
                }
                properties.forEach(p => {
                    oldResult[p] = r[p];
                });
            });

            // filter and sort the results
            this.filterResults({
                filterType: this.filterType
            });
        },
        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-5}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        expandResultText: function(msgData) {
            if (! msgData) return;
            const teamId = msgData.teamId;
            if (! teamId) return;
            const r = this.shownResults.find(r => r.teamId == teamId);
            if (! r) {
                console.log('homework result of team', teamId, 'does not in the current shown result list, ignore the expanded message');
                return;
            }
            r.expanded = true;
        },

        /**
         * @see  {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-6}
         * @param  {[type]} msgData [description]
         * @return {[type]}         [description]
         */
        collapseResultText: function(msgData) {
            if (! msgData) return;
            const teamId = msgData.teamId;
            if (! teamId) return;
            const r = this.shownResults.find(r => r.teamId == teamId);
            if (! r) {
                console.log('homework result of team', teamId, 'does not in the current shown result list, ignore the collapse message');
                return;
            }
            r.expanded = false;
        },
        /**
         * Show homework results list
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-1}
         * @param  {Object} msgData the message data
         * @return {[type]}         [description]
         */
        showResults: function(msgData) {
            this.filesType = '';
            this.hasFiles = false;
            if ( ! msgData) return;
            this.serverTimestamp = msgData.serverTimestamp;

            const hw = msgData.homework;
            if ( ! hw) return;

            ['id',
             'createrId',
             'clazzCourseId',
             'title',
             'status',
             'fullScore',
             'teamType',
             'appraiseType',
             'endTime',
             'autoEnd',
             'anonymousFlag',
             'deadlineTime'].forEach(p => this[p] = hw[p]);

            this.mixAppraiseItems = JSON.parse(JSON.stringify(hw.mixAppraiseItems));
            if (this.mixAppraiseItems) {
                this.mixAppraiseItems.forEach(i => {
                    this.mixPlan[i.type] = i.weight;
                });
            } else {
                this.mixPlan.TEACHER = 0;
                this.mixPlan.TEAM_INTER = 0,
                this.mixPlan.TEAM_OUTER = 0;
            }

            const results = JSON.parse(JSON.stringify(hw.results));
            results.forEach(r => this.preprocessResult(r));

            this.results = results;

            this.calcTimerRequired();

            this.filterResults({
                filterType: this.filterType
            });
        },
            
        /**
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-2}
         * @param  {Object} msgData the message data
         * @return {void}         
         */
        filterResults: function(msgData) {
            const filterType = msgData.filterType;
            if (['ALL', 'APPRAISED', 'NOT_APPRAISE'].indexOf(filterType) === -1) {
                console.log('unknown filter type', filterType);
                return;
            }
            this.filterType = filterType;

            switch (filterType) {
                case 'ALL':
                    this.shownResults = this.results;
                    break;
                case 'APPRAISED':
                    this.shownResults = this.results.filter(r => this.hasAppraised(r));
                    break;
                case 'NOT_APPRAISE':
                    this.shownResults = this.results.filter(r => !(this.hasAppraised(r)));
                    break;
                default:
                    // statements_def
                    break;
            }
            console.log('filter completed, ready to show~~~');

            this.sortResults({
                sortBy: this.sortBy,
                sortType: this.sortType
            });
        },

        /**
         * Handle hw.sortResults message
         * @see {@link https://p.mbook.cc/w/screenx/000-msg/01projector/06hw/#6-3}
         * @param  {Object} msgData the message data
         * @return {void}    
         */
        sortResults: function(msgData) {
            if ( ! msgData) return;
            let sortBy = msgData.sortBy;
            let sortType = msgData.sortType;
            if (['STUDENT_NO', 'SCORE', 'SUBMIT_TIME'].indexOf(sortBy) === -1) {
                console.log('unknown sort by', sortBy);
                return;
            }

            if (sortType !== 'ASC' && sortType !== 'DESC') sortType = 'ASC';

            this.sortBy = sortBy;
            this.sortType = sortType;
            let teamType = this.teamType;

            const studentNoComparator = (a, b) => {
                if (a === b) return 0;
                if (a == null && b != null) return -1;
                if (a != null && b == null) return 1;
                if (teamType === 'NO') {
                    let s1 = a.user.studentNo;
                    let s2 = b.user.studentNo;
                    if (! s1) s1 = '';
                    if (! s2) s2 = '';
                    let d = s1.localeCompare(s2);
                    if (d === 0) {
                        s1 = a.user.fullName;
                        s2 = b.user.fullName;
                        if (! s1) s1 = '';
                        if (! s2) s2 = '';
                        d = s1.localeCompare(s2);
                    }
                    return d;
                } else {
                    let s1 = a.teamTitle;
                    let s2 = b.teamTitle;
                    if (! s1) s1 = '';
                    if (! s2) s2 = '';
                    return s1.localeCompare(s2);
                }
            };

            const submitTimeComparator = (a, b) => {
                if (a === b) return 0;
                if (a == null && b != null) return -1;
                if (a != null && b == null) return 1;
                let s1 = a.lastSubmitTime;
                let s2 = b.lastSubmitTime;
                if (! s1) s1 = '';
                if (! s2) s2 = '';
                let d = s1.localeCompare(s2);
                return d;
            };

            const scoreComparator = (a, b) => {
                if (a === b) return 0;
                if (a == null && b != null) return -1;
                if (a != null && b == null) return 1;

                let s1 = this.calcFinalScore(a);
                let s2 = this.calcFinalScore(b);

                if (s1 === -1 && s1 >= 0) return -1;
                if (s1 >= 0 && s2 === -1) return 1;
                let d = s1 - s2;
                return d;
            };

            this.shownResults.sort((a, b) => {
                let d = 0;
                switch (sortBy) {
                    case 'STUDENT_NO':
                        d = studentNoComparator(a, b);
                        break;
                    case 'SUBMIT_TIME':
                        d = submitTimeComparator(a, b);
                        break;
                    case 'SCORE':
                        d = scoreComparator(a, b);
                        break;
                    default:
                        // statements_def
                        break;
                }

                if (d != 0) return d * (sortType === 'DESC' ? -1 : 1);
                // 如果是按照提交时间或者得分排序，当 a 和 b 相等的时候，按照学号或者组号的自然顺序排序，此时不关心 ASC 还是 DESC
                if (sortBy === 'SUBMIT_TIME' || sortBy === 'SCORE') {
                    return studentNoComparator(a, b);
                }
            });
        },

        /**
         * Calc a result's final score
         * @param  {Object} result       the result
         * @return {Number}              -1 means not appraised
         */
        calcFinalScore: function(result) {
            const teamType = this.teamType;
            const appraiseType = this.appraiseType;
            const status = this.status;
            const mixPlan = {};

            if (this.mixAppraiseItems) {
                this.mixAppraiseItems.forEach(i => {
                    mixPlan[i.type] = i.weight;
                });
            }

            if (appraiseType === 'TEACHER') return result.appraiseScore;

            if (appraiseType === 'APPRAISER') {
                return result.teacherAppraiseScore >= 0 ? result.teacherAppraiseScore : result.appraiseScore;
            }

            if (appraiseType === 'EACH_OTHER') {
                if (status === 'APPRAISEING' || status === 'END') {
                    return result.teacherAppraiseScore >= 0 ? result.teacherAppraiseScore : result.appraiseScore;   
                }
                
                return -1;
            }

            if (appraiseType === 'MIX') {
                if (result.mixTeacherAppraiseScore < 0 && result.mixTeamInterAppraiseScore < 0 && result.mixTeamOuterAppraiseScore < 0) return -1;
                
                let score = 0;
                if (status === 'APPRAISEING' || status === 'END') {
                    if (result.mixTeacherAppraiseScore >= 0 && ('TEACHER' in mixPlan)) score += result.mixTeacherAppraiseScore * mixPlan.TEACHER;
                    if (result.mixTeamOuterAppraiseScore >= 0 && ('TEAM_OUTER' in mixPlan)) score += result.mixTeamOuterAppraiseScore * mixPlan.TEAM_OUTER;
                    if (teamType === 'NO' && result.mixTeamInterAppraiseScore && ('TEAM_INTER' in mixPlan)) score += result.mixTeamInterAppraiseScore * mixPlan.TEAM_INTER;

                    return score;
                }
                return -1;
            }
        },

        /**
         * Test if a result is appraised or not
         * @param  {Object}  r homework result
         * @return {Boolean}        
         */
        hasAppraised: function(r) {
            if (this.appraiseType === 'MIX') {
                if (this.teamType === 'NO') {
                    return (this.mixPlan.TEACHER > 0 ? (r.mixTeacherAppraiseScore >= 0) : true)
                        && (this.mixPlan.TEAM_INTER > 0 ? (r.mixTeamInterAppraiseScore >= 0) : true)
                        && (this.mixPlan.TEAM_OUTER > 0 ? (r.mixTeamOuterAppraiseScore >= 0) : true);
                } else {
                    return (this.mixPlan.TEACHER > 0 ? (r.mixTeacherAppraiseScore >= 0) : true)
                        && (this.mixPlan.TEAM_INTER > 0 ? (r.mixTeamInterAppraiseCount > 0) : true)
                        && (this.mixPlan.TEAM_OUTER > 0 ? (r.mixTeamOuterAppraiseScore >= 0) : true);
                }
            } else {
                return (r.appraiseScore >= 0 || r.teacherAppraiseScore >= 0);
            }
        },

        /**
         * Calculate if the timer is required
         * @return {void} No return value, just this.timerRequired is modified or modified later
         */
        calcTimerRequired: function() {
            if (this.timerId > 0) clearTimeout(this.timerId);

            if ((this.status !== 'IN_PRGRS' && this.status !== 'APPRAISEING') || !this.deadlineTime) {
                this.timerRequired = false;
                return;
            }

            if (! this.deadlineTime) {
                console.log('invalid auto end time found');
                return;
            }

            const now = Date.now();
            const endTime = (new Date(this.deadlineTime)).getTime();
            if (now >= endTime) {
                this.timerRequired = false;
                return;
            }

            const d = endTime - now;
            if (d <= 3600000) {
                this.timerRequired = true;
                this.timeInSeconds = Math.round(d / 1000);
                return;
            }

            this.timerId = setTimeout(() => {
                this.timerRequired = true;
                this.timeInSeconds = 3600;
            }, (endTime - now) - 3600000);
        },

        moveJoystick: function(msgData) {
            if (this.hasFiles) return;
            let direction = msgData.direction;
            let box = this.$refs.box;

            if (this.hintData == 'start') return;
            if (direction === 'LEFT') {
                return;
            } else if (direction === 'RIGHT') {
                return;
            } else if (direction === 'UP') {
                box.scrollTop -= 40;
            } else if (direction === 'DOWN') {
                box.scrollTop += 40;
            } else {
                return;
            }

        },

        handleHwFilesShow: function(msgData) {
            // clearTimeout(timer);
            this.showResourceView(msgData.data);
            this.hwFilesData = msgData;
            this.hasFiles = true;
            // if (this.filesType === 'audio') return;
            // this.laters = false;
            // let timer = setTimeout(() => {
            //     this.laters = true;
            // }, 3000);
        },

        showResourceView: function(res) {
            clearTimeout(timer);
            this.laters = false;

            let mimeType = res.mimeType;
            let previewableDocument = res.previewableDocument;
            let filesType = '';
            if (! mimeType) mimeType = 'unknown';
            mimeType = mimeType.toLowerCase();
            if (mimeType.startsWith('image/')) {
                filesType = 'image';
            } else if (mimeType.startsWith('audio/')) {
                filesType = 'audio';
            } else if (mimeType === 'application/x-mpegurl' || mimeType.startsWith('video/')) {
                filesType = 'vidio';
            } else if (mimeType === 'mosoink/html') {
                filesType = 'html';
            } else if (mimeType === 'mosoink/url') {
                filesType = 'nonsupport';
            } else if (mimeType === 'text/plain' || tool.getCodeLang(res.extName) != null) {
                filesType = 'text';
            } else {
                if (previewableDocument) {
                    filesType = 'pdf';
                } else {
                    console.log('无法正常读取文件');
                    filesType = 'nonsupport';
                }
            }

            this.filesType = filesType;
            
            let timer = setTimeout(() => {
                if (this.filesType === 'audio') return;
                this.laters = true;
            }, 3000);
        },

        handleHwFilesHide: function(msgData) {
            this.filesType = '';
            this.hasFiles = false;
        },

    }
};
</script>


<style type="text/css" scoped>
    .clearfix:after {
        visibility: hidden;
        display: block;
        font-size: 0;
        content: " ";
        clear: both;
        height: 0;
    }

    .hw-box {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        overflow-x: hidden;
        overflow-y: auto;
    }


    .title-bar {
        position: fixed;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        height: 80px;
        padding: 20px 40px 20px 40px;
        background-color: #191919;
        width: 100%;
        box-sizing: border-box;
        z-index: 9;
    }

    .title-bar .led-timer {
        flex-shrink: 0;
        flex-grow: 0;
    }

    .title-bar .title {
        flex-grow: 1;
        height: 40px;
        line-height: 40px;
        vertical-align: middle;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        text-align: center;
        font-size: 24px;
        flex-grow: 1;
        color: #ffffff;
    }

    .title-bar .stat-info {
        flex-grow: 0;
        flex-shrink: 0;
        padding-left: 40px;
        text-align: right;
        line-height: 36px;
        vertical-align: middle;
    }

    .title-bar .stat-info .label {
        flex-grow: 1;
        font-size: 18px;
        color: #BBC2C6;
        vertical-align: middle;
        padding: 0 10px;
    }

    .title-bar .stat-info .data {
        display: inline-block;
        font-size: 30px;
        color: #ffffff;
        vertical-align: middle;
    }

    .hint-box {
        position: absolute;
        top: 80px;
        left: 0;
        right: 0;
        bottom: 0;

        display: flex;
        justify-content: center;
        align-items: center;
    }

    .hint-box .hint-content {
        flex-grow: 0;
        flex-shrink: 0;
        padding-top: 230px;
        background-image: url('../assets/image/hw-no-result.png');
        background-size: 200px 200px;
        background-repeat: no-repeat;
        background-position: center top;
        font-size: 24px;
        color: #fff;
    }

    .content-box {
        padding-top: 80px;
    }

    .op-bar {
        padding-left: 40px;
        padding-right: 40px;
        height: 80px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    .op-bar .filter-by {
        height: 36px;
        line-height: 36px;
        vertical-align: middle;
        padding: 0 30px 0 30px;
        color: #BBC2C6;
        font-size: 24px;
        border: 2px solid #bbc2c6;
        border-radius: 20px;
        flex-shrink: 0;
        flex-grow: 0;
    }
    
    .op-bar .sort-by {
        font-size: 18px;
        line-height: 100%;
        flex-shrink: 0;
        flex-grow: 1;
        text-align: right;
        color: #BBC2C6;
        
    }
    
    .op-bar .sort-by-indicator {
        width: 12px;
        height: 24px;
        margin-left: 10px;
        flex-grow: 0;
        flex-shrink: 0;
        background-image: url('../assets/image/sort-icon.png');
        background-size: 24px 24px;
        background-repeat: no-repeat;
        background-position: 0 0;

    }
    .op-bar .sort-by-indicator.desc {
        background-position: -12px 0;
    }


    .results-container {
        padding: 10px 40px 40px 40px;
    }

    .result-box {
        padding: 40px;
        background-color: #444;
        border-radius: 10px;
        margin-bottom: 30px;
    }

    .result-box .user-row {
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    .result-box .user-row .avatar {
        width: 42px;
        height: 42px;
        border: 4px solid #fff;
        border-radius: 25px;
        flex-grow: 0;
        flex-shrink: 0;
    }

    .result-box .user-row .name-sno {
        font-size: 24px;
        line-height: 100%;
        color: #fff;
        text-align: left;
        margin-left: 20px;
    }
    
    .result-box.team .user-row .name-sno {
        margin-left: 0;
    }

    .result-box .content {
        padding: 20px 0 30px 0px;
        margin-left: 70px;
        color: #fff;
        font-size: 1.5rem;
        line-height: 150%;
        text-align: left;
    }
    
    .result-box.team .content {
        margin-left: 0;
    }

    .result-box .info {
        border-top: 2px solid rgba(0, 0, 0, 0.3);
        padding-top: 20px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-left: 70px;
    }

    .result-box.team .info {
        margin-left: 0;
    }

    .result-box .info .left-col,
    .result-box .info .right-col {
        flex-shrink: 0;
        flex-grow: 1;
    }

    .result-box .info .left-col {
        text-align: left;
    }

    .result-box .info .right-col {
        text-align: right;
    }

    .result-box .info .left-col span,
    .result-box .info .right-col span {
        font-size: 18px;
        color: #bbc2c6;
    }

    .result-box .info .left-col span.warning {
        color:  #FF9900;
    }

    .result-box .info .right-col span.score {
        color: #8FC31F;
    }

    .result-box .atta-box {
        margin-left: 50px;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
    }

    .result-box .atta-box.empty-content {
        padding-top: 20px;
    }

    .result-box.team .atta-box {
        margin-left: -20px;
    }

    .result-box .atta-box .atta {
        width: 80px;
        height: 80px;
        margin-left: 20px;
        margin-bottom: 20px;
        flex-grow: 0;
        flex-shrink: 0;
        background-color: #252525;
        overflow: hidden;
        background-size: 80px 80px;
        background-repeat: no-repeat;
        position: relative;
    }

    .result-box .atta-box .atta img {
        width: 80px;
        height: 80px;
    }
    
    .result-box .atta-box .atta.video {
        
    }

    .result-box .atta-box .atta.video:after {
        content: ' ';
        display: block;
        position: absolute;
        right: 5px;
        bottom: 5px;
        width: 20px;
        height: 20px;
        background-image: url('../assets/image/atta-video.png');
        background-size: 20px 20px;
        background-repeat: no-repeat;
    }

    .result-box .atta-box .atta.audio {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_audio.png');
    }

    .result-box .atta-box .atta.excel {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_excel.png');
    }
    
    .result-box .atta-box .atta.other {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_other.png');
    }

    .result-box .atta-box .atta.pdf {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_pdf.png');
    }

    .result-box .atta-box .atta.ppt {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_ppt.png');
    }

    .result-box .atta-box .atta.text {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_text.png');
    }

    .result-box .atta-box .atta.word {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_word.png');
    }

    .result-box .atta-box .atta.zip {
        background-image: url('https://static-cdn-oss.mosoteach.cn/mssvc/file-type-icons-v2/icon_res_zip.png');
    }

    /* homework result detail layer */
    .result-detail-layer {
        padding-top: 80px;
        background-color: #252525;
    }

    .result-detail-layer .result-detail-box {
        padding: 40px 70px;
    }

    .result-detail-box .detail-info {
        height: 80px;
        display: flex;
        justify-content: flex-start;
    }

    .result-detail-box .detail-info .avatar {
        display: block;
        width: 72px;
        height: 72px;
        border-radius: 40px;
        border: 4px solid #ffffff;
        flex-shrink: 0;
        flex-grow: 0;
        margin-right: 20px;
    }
    
    .result-detail-box.team .detail-info .avatar {
        display: none;
    }

    .result-detail-box .detail-info .detail-name-sno-box {
        flex-grow: 0;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
    }

    .result-detail-box .detail-info .detail-name-sno-box > div {
        text-align: left;
        font-size: 24px;
        line-height: 100%;
        color: #ffffff;
        margin-bottom: 12px;
    }

    .result-detail-box .detail-info .detail-name-sno-box > div:last-child {
        margin-bottom: 0;
    }

    .result-detail-box .content {
        padding: 40px 0 0 0;
        color: #fff;
        font-size: 1.5rem;
        line-height: 150%;
        text-align: left;
    }

    .user-info {
        width: 100%;
        height: 80px;
        background: rgba(0, 0, 0, 0.5);
        padding: 0 40px;
        box-sizing: border-box;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 999;
        transition: opacity 400ms linear;
    }

    .user-info-opacity {
        opacity: 0;
    }

    .user-info > div > img {
        width: 50px;
        height: 50px;
        border: 4px solid #ffffff;
        border-radius: 50%;
        box-sizing: border-box;
        margin-right: 20px;
    }

    .user-info > p {
        font-size: 24px;
        color: #FFFFFF;
        margin-right: 10px;
    }

    #hw-img {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: rgba(0, 0, 0, 0.8);
    }

    #hw-audio {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: #252425;
    }

    #hw-1 {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: #252425;
        z-index: 99;
    }

    #hw-text {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: #252425;
    }

    #hw-pdf {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: #252425;
    }

    #hw-nonsupport {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 99;
        background: #252425;
    }
</style>